import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { forkJoin, Observable, of } from 'rxjs';
import { Contact, ContactState, ContactCycle, ContactOrigin, ContactAttributeCustomResponse } from '../models/contacto.model';
import { ModalComponent } from '../../../shared/components/ui/modal/modal.component';
import { DirectoryUser } from '../../../shared/services/user-directory-api.service';
import { QuickActionsMenuComponent } from '../../../shared/components/quick-actions-menu/quick-actions-menu.component';
import { ContactCompanyApiService } from '../../empresas/services/contact-company-api.service';
import { ContactCompanyResponse, CompanySummary } from '../../empresas/models/empresa.model';
import { AuthService } from '../../auth-pages/services/auth.service';
import { AuthApiService } from '../../auth-pages/services/auth-api.service';
import { ContactoApiService } from '../services/contacto-api.service';
import { DatetimeService } from '../../../shared/services/datetime.service';
import { AuditApiService } from '../../audit/services/audit-api.service';
import { AuditLogResponse } from '../../audit/models/audit.model';
import { AuditTimelineComponent } from '../../../shared/components/audit/audit-timeline.component';
import { ContactoCorreosComponent } from '../../correos/contacto-correos/contacto-correos.component';
import { WorkflowRecordHistoryComponent } from '../../automation/workflows/workflow-record-history/workflow-record-history.component';

@Component({
  selector: 'app-contacto-detail-modal',
  standalone: true,
  imports: [
    CommonModule,
    ModalComponent,
    QuickActionsMenuComponent,
    AuditTimelineComponent,
    ContactoCorreosComponent,
    WorkflowRecordHistoryComponent
  ],
  templateUrl: './contacto-detail-modal.component.html'
})
export class ContactoDetailModalComponent implements OnChanges {
  @Input() isOpen = false;
  @Input() contact: Contact | null = null;
  @Input() contactStates: ContactState[] = [];
  @Input() contactCycles: ContactCycle[] = [];
  @Input() contactOrigins: ContactOrigin[] = [];
  @Output() close = new EventEmitter<void>();

  // Companies associated with this contact
  companies: ContactCompanyResponse[] = [];
  loadingCompanies = false;
  companiesError = '';

  registeredByUser: any = null;
  ownerUser: any = null;
  loadingUsers = false;

  // Custom attributes
  customAttributes: ContactAttributeCustomResponse[] = [];
  loadingCustomAttributes = false;

  // Audit timeline
  activeTab: 'info' | 'timeline' | 'correos' | 'automatizaciones' = 'info';
  timelineLogs: AuditLogResponse[] = [];
  loadingTimeline = false;
  timelineError = '';
  timelinePage = 0;
  timelineTotalPages = 0;
  timelineTotalElements = 0;

  constructor(
    private contactCompanyApi: ContactCompanyApiService,
    private contactoApi: ContactoApiService,
    private authService: AuthService,
    private authApiService: AuthApiService,
    private datetimeService: DatetimeService,
    private auditApi: AuditApiService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen'] || changes['contact']) {
      if (this.isOpen && this.contact?.id) {
        this.cargarUsuarios();
        this.cargarEmpresas(this.contact.id);
        this.cargarAtributosCustom(this.contact.id);
      } else if (!this.isOpen) {
        this.companies = [];
        this.companiesError = '';
        this.loadingCompanies = false;
        this.customAttributes = [];
        this.loadingCustomAttributes = false;
        this.registeredByUser = null;
        this.ownerUser = null;
        this.activeTab = 'info';
        this.timelineLogs = [];
        this.timelineError = '';
        this.timelinePage = 0;
      }
    }
  }

  private cargarUsuarios(): void {
    const registerId = this.contact?.userRegisterId;
    const ownerId = this.contact?.userPropietyId;

    if (!registerId && !ownerId) return;

    this.loadingUsers = true;
    this.registeredByUser = null;
    this.ownerUser = null;

    const currentUser = this.authService.getCurrentUser();
    // El usuario en sesión ya está en memoria: se evita la llamada. Se normaliza al shape del
    // directorio (`mail`, no `email`) para que ambas ramas devuelvan exactamente lo mismo.
    const sessionUser: DirectoryUser | null = currentUser
      ? {
          id: currentUser.id,
          fullname: currentUser.fullname,
          lastname: currentUser.lastname,
          mail: currentUser.email,
        }
      : null;
    const resolve = (id: number | undefined): Observable<DirectoryUser | null> =>
      id && id === currentUser?.id
        ? of(sessionUser)
        : id
          ? this.authApiService.getUserById(id)
          : of(null);

    if (registerId === ownerId) {
      // Mismo ID — una sola resolución (puede ser currentUser o una llamada)
      resolve(registerId).subscribe({
        next: (user) => {
          this.registeredByUser = user;
          this.ownerUser = user;
          this.loadingUsers = false;
        },
        error: () => { this.loadingUsers = false; }
      });
    } else {
      forkJoin([resolve(registerId), resolve(ownerId)]).subscribe({
        next: ([registerUser, ownerUser]) => {
          this.registeredByUser = registerUser;
          this.ownerUser = ownerUser;
          this.loadingUsers = false;
        },
        error: () => { this.loadingUsers = false; }
      });
    }
  }

  private cargarEmpresas(idContact: number): void {
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.loadingCompanies = true;
    this.companiesError = '';

    this.contactCompanyApi.getByContact(idContact, businessId).subscribe({
      next: (associations: ContactCompanyResponse[]) => {
        this.companies = associations;
        this.loadingCompanies = false;
      },
      error: () => {
        this.companiesError = 'Error al cargar empresas asociadas';
        this.loadingCompanies = false;
      }
    });
  }

  private cargarAtributosCustom(idContact: number): void {
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.loadingCustomAttributes = true;
    this.contactoApi.getContactCustomAttributes(idContact, businessId).subscribe({
      next: (attrs) => {
        this.customAttributes = attrs;
        this.loadingCustomAttributes = false;
      },
      error: () => { this.loadingCustomAttributes = false; }
    });
  }

  getCustomAttributeDisplayValue(attr: ContactAttributeCustomResponse): string {
    switch (attr.type) {
      case 'text':   return attr.strValue   || '—';
      case 'number': return attr.intValue   != null ? String(attr.intValue) : '—';
      case 'boolean': return attr.bolValue  != null ? (attr.bolValue ? 'Sí' : 'No') : '—';
      case 'list':   return attr.strListValue || '—';
      default:       return attr.strValue || attr.strListValue || String(attr.intValue ?? '') || '—';
    }
  }

  getCompanyInitials(assoc: ContactCompanyResponse): string {
    const name = assoc.company?.commercialName || '';
    return name.substring(0, 2).toUpperCase();
  }

  getCompanyAvatarColor(assoc: ContactCompanyResponse): string {
    const colors = [
      'bg-brand-100 text-brand-600 dark:bg-brand-900/30 dark:text-brand-400',
      'bg-pink-100 text-pink-600 dark:bg-pink-900/30 dark:text-pink-400',
      'bg-cyan-100 text-cyan-600 dark:bg-cyan-900/30 dark:text-cyan-400',
      'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400',
      'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400',
      'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400',
      'bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400',
      'bg-error-100 text-error-600 dark:bg-error-900/30 dark:text-error-400',
    ];

    const name = assoc.company?.commercialName || '';
    const index = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[index % colors.length];
  }

  get initials(): string {
    if (!this.contact) return '';

    const first = this.contact.fullname?.charAt(0) || '';
    const last = this.contact.lastname?.charAt(0) || '';

    return (first + last).toUpperCase();
  }

  get avatarColorClass(): string {
    if (!this.contact?.fullname) return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';

    const colors = [
      'bg-brand-100 text-brand-600 dark:bg-brand-900/30 dark:text-brand-400',
      'bg-pink-100 text-pink-600 dark:bg-pink-900/30 dark:text-pink-400',
      'bg-cyan-100 text-cyan-600 dark:bg-cyan-900/30 dark:text-cyan-400',
      'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400',
      'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400',
      'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400',
      'bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400',
      'bg-error-100 text-error-600 dark:bg-error-900/30 dark:text-error-400',
    ];

    const index = this.contact.fullname
      .split('')
      .reduce((acc, char) => acc + char.charCodeAt(0), 0);

    return colors[index % colors.length];
  }

  get fullName(): string {
    if (!this.contact) return '';
    const parts = [this.contact.fullname, this.contact.lastname].filter(Boolean);
    return parts.join(' ');
  }

  getContactStateName(): string {
    if (!this.contact?.idContactState) return 'Sin contacto';
    const state = this.contactStates.find(s => s.id === this.contact?.idContactState);
    return state?.name || 'Sin contacto';
  }

  getCycleName(): string {
    if (!this.contact?.idContactCycle) return 'Sin ciclo';
    const cycle = this.contactCycles.find(c => c.id === this.contact?.idContactCycle);
    return cycle?.name || 'Sin ciclo';
  }

  getOriginName(): string {
    if (!this.contact?.idOrigin) return 'Sin origen';
    const origin = this.contactOrigins.find(o => o.id === this.contact?.idOrigin);
    return origin?.name || 'Sin origen';
  }

  formatDate(dateString?: string): string {
    return this.datetimeService.formatDateTime(dateString);
  }

  /**
   * inc37 — "Creado por" de un contacto que nadie creó a mano.
   *
   * Un contacto nacido de un formulario público, del widget de chat o de una reserva no
   * tiene usuario detrás: el backend deja `userRegisterId` nulo a propósito. Eso no es
   * "sin información" (que suena a dato perdido) sino un autor concreto y conocido: el
   * sistema. Solo cuando SÍ hay id pero no se pudo resolver el usuario (borrado, otro
   * negocio) se sigue diciendo "Sin información", que ahí sí es la verdad.
   */
  get createdByLabel(): string {
    if (this.registeredByUser) {
      return `${this.registeredByUser.fullname} ${this.registeredByUser.lastname || ''}`.trim();
    }
    return this.contact?.userRegisterId ? 'Sin información' : 'Sistema';
  }

  /** inc38 — mismo criterio para el responsable: sin dueño asignado ≠ dato perdido. */
  get ownerLabel(): string {
    if (this.ownerUser) {
      return `${this.ownerUser.fullname} ${this.ownerUser.lastname || ''}`.trim();
    }
    return this.contact?.userPropietyId ? 'Sin información' : 'Sin asignar';
  }

  // ── Timeline ──

  onTabChange(tab: 'info' | 'timeline' | 'correos' | 'automatizaciones'): void {
    this.activeTab = tab;
    if (tab === 'timeline' && this.timelineLogs.length === 0 && !this.loadingTimeline) {
      this.loadTimeline(0);
    }
  }

  loadTimeline(page = 0): void {
    if (!this.contact?.id) return;
    this.loadingTimeline = true;
    this.timelineError = '';
    this.timelinePage = page;

    // Endpoint /timeline: unifica los eventos del contacto con los de sus sub-entidades
    // (empresas asociadas, atributos personalizados). El backend deriva businessId del JWT
    // y enriquece cada fila con changesDisplay (etiquetas en español + valores legibles).
    this.auditApi.getEntityTimeline('contact', String(this.contact.id), { page, size: 15 }).subscribe({
      next: data => {
        this.timelineLogs = data.content ?? [];
        this.timelinePage = data.page ?? page;
        this.timelineTotalPages = data.totalPages ?? 1;
        this.timelineTotalElements = data.totalElements ?? 0;
        this.loadingTimeline = false;
      },
      error: () => {
        this.timelineError = 'Error al cargar la línea de tiempo.';
        this.loadingTimeline = false;
      }
    });
  }

  // La presentación de los eventos (traducciones, diffs, títulos) y el
  // expand/colapso por log viven en AuditTimelineComponent / AuditDisplayService.

  onClose(): void {
    this.close.emit();
  }
}
